import {
  build_case_form_sections,
  build_stage_timeline,
  build_summary_rows,
  fetch_all_onboarding_cases,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  WORKFLOW_ACTION_STATE,
  workflowActionComputed,
  workflowActionMethods
} from '@/services/onboarding_workflow_action_service';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import { resolve_onboarding_journey_filter_key } from '@/services/onboarding_journey_filter_service';
import {
  hide_onboarding_system_loader_after_render,
  show_onboarding_system_loader
} from '@/views/onboarding_system_views/onboarding_ui_helpers';
import {
  ACTIVATION_STATE,
  action_class,
  build_detail_tabs,
  decision_badge_class,
  filter_activation_cases,
  get_activation_decision,
  get_activation_means,
  manageActivationComputed,
  map_case_to_activation_item,
  progress_bar_color,
  selected_case_subtitle,
  tab_icon_for_section,
  tab_id_for_section
} from './manage_activation_helpers';

export default {
  name: 'OnboardingManageActivation',
  data: () => ({
    ...ACTIVATION_STATE,
    ...WORKFLOW_ACTION_STATE,
    ...tablePaginationState()
  }),
  computed: {
    ...workflowActionComputed,
    ...manageActivationComputed,
    ...tablePaginationComputed
  },
  mounted() {
    this.load_activation_queue();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  watch: {
    active_journey_filter() {
      this.close_activation_detail();
      this.load_activation_queue();
    }
  },
  methods: {
    ...workflowActionMethods,
    ...tablePaginationMethods,
    resolve_onboarding_journey_filter_key,
    handle_onboarding_data_updated() {
      this.load_activation_queue();
    },
    get_workflow_action_case() {
      return this.selected_case || null;
    },
    async on_workflow_action_success() {
      this.close_activation_detail();
      await this.load_activation_queue();
    },
    get_activation_means(journey) {
      return get_activation_means(journey);
    },
    get_activation_decision(case_item) {
      return get_activation_decision(case_item);
    },
    map_case_to_activation_item(case_item) {
      return map_case_to_activation_item(case_item);
    },
    async load_activation_queue() {
      this.is_loading_queue = true;
      this.queue_error = '';
      show_onboarding_system_loader('Loading activation queue...');

      try {
        const cases = await fetch_all_onboarding_cases();
        this.queue = filter_activation_cases(cases, this.active_journey_filter);
      } catch (error) {
        this.queue = [];
        this.queue_error = error?.response?.data?.message || error?.message || 'Unable to load activation queue.';
      } finally {
        this.is_loading_queue = false;
        hide_onboarding_system_loader_after_render(this);
      }
    },
    decision_badge_class(tone) {
      return decision_badge_class(tone);
    },
    action_class(tone) {
      return action_class(tone);
    },
    tab_id_for_section(form, section) {
      return tab_id_for_section(form, section);
    },
    build_detail_tabs(form_sections = []) {
      return build_detail_tabs(form_sections);
    },
    tab_icon_for_section(form = {}, section = {}) {
      return tab_icon_for_section(form, section);
    },
    selected_case_subtitle() {
      return selected_case_subtitle(this.selected_case, this.selected_activation);
    },
    async open_activation_detail(activation_item) {
      const case_item = activation_item?.case_item;
      if (!case_item) return;

      show_onboarding_system_loader('Loading activation drawer...');
      this.reset_workflow_action_state();
      this.selected_activation = activation_item;
      this.selected_case = case_item;
      this.active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;
      this.summary_rows = build_summary_rows(case_item);
      this.stage_timeline = build_stage_timeline(case_item);
      this.case_form_sections = build_case_form_sections(case_item);
      this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
      this.is_loading_detail = true;

      try {
        const hydrated_case = await hydrate_case_detail(case_item);
        this.selected_case = hydrated_case;
        this.summary_rows = build_summary_rows(hydrated_case);
        this.stage_timeline = build_stage_timeline(hydrated_case);
        this.case_form_sections = build_case_form_sections(hydrated_case);
        this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
        await this.load_workflow_actions_for_case(hydrated_case);
      } catch {
        this.selected_case = case_item;
        await this.load_workflow_actions_for_case(case_item);
      } finally {
        this.is_loading_detail = false;
        hide_onboarding_system_loader_after_render(this);
      }
    },
    close_activation_detail() {
      this.reset_workflow_action_state();
      this.selected_activation = null;
      this.selected_case = null;
      this.active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
    },
    current_detail_action_tab() {
      return this.detail_tabs.find((tab) => String(tab?.badge || '').trim().toLowerCase() === 'current') || this.detail_tabs[0] || null;
    },
    current_detail_action_copy() {
      return 'Confirm that every required gate is complete, check readiness, and decide whether this onboarding can be activated.';
    },
    open_selected_activation_profile() {
      const profile_tab = this.detail_tabs.find((tab) => String(`${tab?.id || ''} ${tab?.label || ''}`).trim().toLowerCase().match(/profile|personal|applicant/));
      this.active_detail_tab = profile_tab?.id || 'summary';
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    progress_bar_color() {
      return progress_bar_color(this.selected_case);
    }
  }
};
